import type { FormEvent } from 'react';
import { Button, Form, Modal, ModalBody, ModalFooter } from '@customerio/pluma-components/react';
import { getWarehouseDestinationName } from '@/features/warehouse-sync';
import type { BucketConfigDraft } from '@/features/warehouse-sync';
import type { WarehouseDestination } from '@/services/db/warehouse-sync/entity';
import { PROVIDER_FORMS } from './provider-form';
import { useBucketConfigDraft } from './provider-forms/use-bucket-config-draft';

type ManageConfigurationProps = {
  destination: WarehouseDestination;
  /** The bucket config as stored, minus whatever the API withholds. */
  storedConfig: BucketConfigDraft;
  isReadOnly: boolean;
  isUpdating: boolean;
  onClose: () => void;
  /** Resolves false when the write failed, so the modal stays open over the form. */
  onUpdate: (config: BucketConfigDraft) => Promise<boolean>;
};

/**
 * Rotates the bucket credentials of a connected sync.
 *
 * Mounted only while open — that is what discards an abandoned draft and re-seeds
 * the next one from the record, including after a successful save.
 *
 * The API never returns a stored secret, so the secret fields open blank and have
 * to be re-entered; the `update` validation mode says so in as many words.
 */
export function ManageConfiguration({
  destination,
  storedConfig,
  isReadOnly,
  isUpdating,
  onClose,
  onUpdate,
}: ManageConfigurationProps) {
  const ProviderForm = PROVIDER_FORMS[destination];
  const draft = useBucketConfigDraft({
    destination,
    mode: 'update',
    initialConfig: storedConfig,
    isDisabled: isReadOnly || isUpdating,
  });

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // The button is disabled while errors show, but Enter bypasses a disabled button
    // and an untouched form has raised no errors yet.
    if (isReadOnly || isUpdating || !draft.validateAll()) {
      return;
    }
    const didUpdate = await onUpdate(draft.getConfig());
    if (didUpdate) {
      onClose();
    }
  };

  return (
    <Modal
      data-test-warehouse-modal="manage-configuration"
      title={`Configure your ${getWarehouseDestinationName(destination)} bucket`}
      isOpen
      onClose={onClose}
    >
      <Form data-test-warehouse-manage-configuration-form={destination} onSubmit={onSubmit}>
        <ModalBody>
          <ProviderForm {...draft.form} />
        </ModalBody>

        <ModalFooter>
          <Button data-test-warehouse-button="cancel" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-test-warehouse-button="save-configuration"
            type="submit"
            variant="primary"
            isDisabled={isReadOnly || draft.hasErrors}
            isLoading={isUpdating}
          >
            {isUpdating ? 'Updating…' : 'Update configuration'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
